import { Formik } from 'formik'
import React, { useState } from 'react'
import { InstallationLocationChangeForm } from '../models'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import ChangeLocationForm from './ChangeLocationForm'
import { useChangeLocationFormInitializer } from '../hooks'
import { InformationBox } from 'src/Components'
import { Trans } from 'react-i18next'

export interface ChangeLocationDialogProps {
  place?: number
  onClose?: () => void
  onConfirm: (values: InstallationLocationChangeForm) => void
  currentCountryCode: string
}

/**
 * Dialog to change the address of an installation
 */
const ChangeLocationDialog: React.FC<ChangeLocationDialogProps> = ({ place, onClose, onConfirm, currentCountryCode }) => {
  const { initialValues, validationSchema } = useChangeLocationFormInitializer()

  const { t } = useCustomTranslation()
  const title = t('installation-details.change-location.title')

  const [warningOpen, setWarningOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<InstallationLocationChangeForm | null>(null)

  const confirmLocationChange = (values: InstallationLocationChangeForm): void => {
    onConfirm(values)
    onClose?.()
  }

  const handleSubmit = (values: InstallationLocationChangeForm): void => {
    if (values.countryCode !== currentCountryCode) {
      setPendingValues(values)
      setWarningOpen(true)
      return
    }

    confirmLocationChange(values)
  }

  const handleWarningConfirm = (): void => {
    if (pendingValues) {
      confirmLocationChange(pendingValues)
    }

    setPendingValues(null)
    setWarningOpen(false)
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ isValid, submitCount, handleSubmit }) => {
        return (
          <>
            <Dialog
              place={place}
              title={title}
              maxWidth="sm"
              onClose={onClose}
              actions={{
                secondary: {
                  name: t('common.cancel'),
                  onClick: () => onClose?.(),
                },
                primary: {
                  name: title,
                  disabled: submitCount > 0 ? !isValid : false,
                  onClick: handleSubmit,
                },
              }}
            >
              <ChangeLocationForm />
            </Dialog>
            {warningOpen && (
              <Dialog
                place={place}
                title={t('installation-details.change-location.warning-title')}
                maxWidth="sm"
                onClose={() => setWarningOpen(false)}
                actions={{
                  secondary: {
                    name: t('common.cancel'),
                    onClick: () => setWarningOpen(false),
                  },
                  primary: {
                    name: t('common.confirm'),
                    onClick: handleWarningConfirm,
                  },
                }}
              >
                <InformationBox
                  text={
                    (<Trans i18nKey="installation-details.change-location.warning-message" components={{ break: <br /> }} />) as unknown as string
                  }
                  messageType="warning"
                />
              </Dialog>
            )}
          </>
        )
      }}
    </Formik>
  )
}

export default ChangeLocationDialog
